import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api.js';
import { AreaTexto, Aviso, Botao, Campo, Carregando, Entrada, Etiqueta, Modal, Paginacao, Selecao, usePaginacao, Vazio } from '../../componentes/ui.jsx';
import { Busca, Chips, MenuAcoes, normalizar } from '../equipe/ListaEquipe.jsx';
import '../Equipe.css';
import './Catalogo.css';

/**
 * Aba de servicos do catalogo.
 *
 * Tres coisas que a tela antiga nao tinha e a empresa pedia:
 *   - CATEGORIAS de verdade: uma coluna com a contagem de cada uma, que filtra
 *     a lista, e onde se renomeia (ou junta) uma categoria de uma vez;
 *   - FILTROS: busca, situacao (ativo / inativo / sem ninguem que execute);
 *   - EDITAR: antes so dava para criar — preco errado era excluir e refazer.
 *
 * Nao ha teto de servicos ativos: com catalogo grande a Sofia recebe as
 * categorias e busca os servicos (buscar_servicos). Por isso nome, categoria
 * e descricao bem preenchidos importam — e por eles que ela acha o servico.
 */

const SITUACOES = [
  { chave: 'todos', rotulo: 'Todos' },
  { chave: 'ativos', rotulo: 'Ativos', ponto: 'sucesso' },
  { chave: 'inativos', rotulo: 'Inativos', ponto: 'neutro' },
  { chave: 'sem-profissional', rotulo: 'Sem profissional', ponto: 'alerta' }
];

const passaNaSituacao = {
  todos: () => true,
  ativos: (s) => s.ativo,
  inativos: (s) => !s.ativo,
  'sem-profissional': (s) => s.profissionais.length === 0
};

/** Centavos -> "45,00" para os campos de dinheiro. */
const emReais = (centavos) => ((Number(centavos) || 0) / 100).toFixed(2).replace('.', ',');

const duracaoLegivel = (min) => (min >= 60 ? `${Math.floor(min / 60)}h${min % 60 ? String(min % 60).padStart(2, '0') : ''}` : `${min} min`);

export function Servicos({ podeEditar, pedidoNovo }) {
  const queryClient = useQueryClient();
  const [busca, setBusca] = useState('');
  const [situacao, setSituacao] = useState('todos');
  const [categoria, setCategoria] = useState(null); // null = todas
  const [ficha, setFicha] = useState(null); // { servico } ou { novo: true, categoria }
  const [renomeando, setRenomeando] = useState(null);

  const consulta = useQuery({ queryKey: ['servicos', 'todos'], queryFn: () => api.get('/api/servicos', { incluirInativos: 'true' }) });
  const todos = consulta.data?.servicos ?? [];
  const ativos = todos.filter((s) => s.ativo).length;

  // O botao "Novo servico" mora no cabecalho da pagina (como na Equipe): cada
  // clique la vira um numero novo, que esta aba escuta.
  const [ultimoPedido, setUltimoPedido] = useState(pedidoNovo);
  if (pedidoNovo !== ultimoPedido) {
    setUltimoPedido(pedidoNovo);
    if (pedidoNovo) setFicha({ novo: true, categoria });
  }

  const categorias = useMemo(() => {
    const mapa = new Map();
    for (const s of todos) mapa.set(s.categoria, (mapa.get(s.categoria) ?? 0) + 1);
    return [...mapa.entries()].sort(([a], [b]) => a.localeCompare(b, 'pt-BR')).map(([nome, total]) => ({ nome, total }));
  }, [todos]);

  // Categoria escolhida que sumiu (renomeada, ultimo servico excluido): volta para "todas".
  const categoriaAtual = categoria && categorias.some((c) => c.nome === categoria) ? categoria : null;

  const termo = normalizar(busca.trim());
  const daCategoria = todos.filter((s) => !categoriaAtual || s.categoria === categoriaAtual);
  const buscados = daCategoria.filter(
    (s) => !termo || normalizar(`${s.nome} ${s.categoria} ${s.profissionais.map((p) => p.nome).join(' ')}`).includes(termo)
  );
  const visiveis = buscados.filter(passaNaSituacao[situacao]);

  // Agrupa por categoria so quando "todas" estao a mostra: dentro de uma
  // categoria, o titulo repetido seria ruido.
  // No maximo 30 por pagina (regra de todas as listas). Ordena por categoria
  // ANTES de paginar: senao uma categoria se espalhava por varias paginas.
  const ordemCategoria = new Map(categorias.map((c, i) => [c.nome, i]));
  const ordenados = categoriaAtual ? visiveis : [...visiveis].sort((a, b) => (ordemCategoria.get(a.categoria) ?? 0) - (ordemCategoria.get(b.categoria) ?? 0));
  const pagServicos = usePaginacao(ordenados);
  const daPagina = pagServicos.itens;
  const grupos = categoriaAtual
    ? [{ nome: null, itens: daPagina }]
    : categorias.map((c) => ({ nome: c.nome, itens: daPagina.filter((s) => s.categoria === c.nome) })).filter((g) => g.itens.length);

  const invalidar = () => {
    queryClient.invalidateQueries({ queryKey: ['servicos'] });
    queryClient.invalidateQueries({ queryKey: ['catalogo', 'metricas'] });
  };

  const alternar = useMutation({
    mutationFn: (s) => api.patch(`/api/servicos/${s.id}`, { ativo: !s.ativo }),
    onSuccess: invalidar
  });
  const excluir = useMutation({
    mutationFn: (s) => api.delete(`/api/servicos/${s.id}`),
    onSuccess: invalidar
  });

  function remover(s) {
    if (confirm(`Excluir "${s.nome}"?\n\nSe ele tiver horários marcados, o sistema pede para desativar em vez de excluir.`)) {
      excluir.mutate(s);
    }
  }

  const erroAcao = alternar.error ?? excluir.error;

  return (
    <div className="coluna">
      {consulta.isLoading ? (
        <Carregando />
      ) : todos.length === 0 ? (
        <Vazio
          titulo="Nenhuma consulta ou procedimento cadastrado"
          descricao="Cadastre as consultas, exames e procedimentos para poder agendar — e para a Sofia saber o que oferecer. Sem serviço cadastrado ela não informa preço nenhum."
          acao={podeEditar && <Botao onClick={() => setFicha({ novo: true })}>Cadastrar o primeiro</Botao>}
        />
      ) : (
        <div className="cat-layout">
          <nav className="cartao cat-categorias" aria-label="Categorias">
            <div className="cat-categorias__titulo">
              <span>Categorias</span>
              {podeEditar && (
                <button
                  type="button"
                  className="cat-categorias__nova"
                  // Categoria nao existe sozinha: nasce com o primeiro servico
                  // dela. Por isso "+ Nova" abre o cadastro ja no campo de nova.
                  title="A categoria nasce com o primeiro serviço dela"
                  onClick={() => setFicha({ novo: true, categoria: '' })}
                >
                  + Nova
                </button>
              )}
            </div>
            <ul>
              <li>
                <button
                  type="button"
                  className={`cat-categoria${!categoriaAtual ? ' cat-categoria--ativa' : ''}`}
                  aria-pressed={!categoriaAtual}
                  onClick={() => setCategoria(null)}
                >
                  <span>Todas</span>
                  <small>{todos.length}</small>
                </button>
              </li>
              {categorias.map((c) => (
                <li key={c.nome} className="cat-categorias__item">
                  <button
                    type="button"
                    className={`cat-categoria${categoriaAtual === c.nome ? ' cat-categoria--ativa' : ''}`}
                    aria-pressed={categoriaAtual === c.nome}
                    onClick={() => setCategoria(c.nome)}
                  >
                    <span>{c.nome}</span>
                    <small>{c.total}</small>
                  </button>
                  {podeEditar && (
                    <button
                      type="button"
                      className="cat-categoria__editar"
                      title={`Renomear "${c.nome}"`}
                      aria-label={`Renomear a categoria ${c.nome}`}
                      onClick={() => setRenomeando(c.nome)}
                    >
                      <svg viewBox="0 0 24 24" aria-hidden="true">
                        <path d="M4 20h4L19 9l-4-4L4 16v4Z" />
                        <path d="m13.5 6.5 4 4" />
                      </svg>
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </nav>

          <div className="coluna cat-principal">
            <div className="eq-ferramentas">
              <Busca valor={busca} aoMudar={setBusca} rotulo="Consulta, categoria ou médico" />
              <Chips
                rotulo="Situação"
                valor={situacao}
                aoMudar={setSituacao}
                opcoes={SITUACOES.map((f) => ({ ...f, total: buscados.filter(passaNaSituacao[f.chave]).length }))}
              />
            </div>

            {erroAcao && <Aviso tom="perigo">{erroAcao.message}</Aviso>}

            <section className="cartao eq-lista cat-lista" aria-label="Serviços">
              <div className="eq-linha eq-linha--cabecalho" aria-hidden="true">
                <span>Serviço</span>
                <span>Duração</span>
                <span>Preço</span>
                <span>Quem faz</span>
                <span>Situação</span>
                <span />
              </div>

              {visiveis.length === 0 ? (
                <p className="eq-lista__vazia">Nenhum serviço com esse filtro.</p>
              ) : (
                grupos.map((g) => (
                  <div key={g.nome ?? 'lista'} role="group" aria-label={g.nome ?? undefined}>
                    {g.nome && (
                      <div className="cat-grupo">
                        {g.nome}
                        <small>{g.itens.length}</small>
                      </div>
                    )}
                    {g.itens.map((s) => (
                      <LinhaServico
                        key={s.id}
                        servico={s}
                        podeEditar={podeEditar}
                        aoAbrir={() => podeEditar && setFicha({ servico: s })}
                        aoAlternar={() => alternar.mutate(s)}
                        aoExcluir={() => remover(s)}
                      />
                    ))}
                  </div>
                ))
              )}

              <Paginacao {...pagServicos} rotulo="serviços" />

              <p className="eq-lista__rodape">
                {visiveis.length === todos.length
                  ? `${todos.length} serviço${todos.length === 1 ? '' : 's'} · ${ativos} ativo${ativos === 1 ? '' : 's'}`
                  : `Mostrando ${visiveis.length} de ${todos.length}`}
              </p>
            </section>
          </div>
        </div>
      )}

      {ficha && (
        <FichaServico
          servico={ficha.servico}
          categoriaInicial={ficha.categoria}
          categorias={categorias.map((c) => c.nome)}
          aoFechar={() => setFicha(null)}
          aoSalvar={() => {
            setFicha(null);
            invalidar();
          }}
        />
      )}

      {renomeando && (
        <ModalRenomearCategoria
          atual={renomeando}
          categorias={categorias.map((c) => c.nome)}
          aoFechar={() => setRenomeando(null)}
          aoSalvar={(para) => {
            setRenomeando(null);
            if (categoriaAtual === renomeando) setCategoria(para);
            invalidar();
          }}
        />
      )}
    </div>
  );
}

function LinhaServico({ servico: s, podeEditar, aoAbrir, aoAlternar, aoExcluir }) {
  const proprios = s.profissionais.filter((p) => p.precoProprio);
  return (
    <div
      className={`eq-linha${s.ativo ? '' : ' eq-linha--inativa'}${podeEditar ? '' : ' eq-linha--travada'}`}
      role={podeEditar ? 'button' : undefined}
      tabIndex={podeEditar ? 0 : undefined}
      onClick={aoAbrir}
      onKeyDown={(e) => podeEditar && (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), aoAbrir())}
      aria-label={podeEditar ? `Editar ${s.nome}` : undefined}
    >
      <span className="eq-pessoa__texto">
        <strong>
          {s.nome} <Etiqueta tom={s.exigeConfirmacao ? 'alerta' : 'neutro'}>{ROTULO_TIPO[s.tipo] ?? 'Consulta'}</Etiqueta>
          {s.iaAgenda === false && <Etiqueta tom="neutro">só recepção</Etiqueta>}
        </strong>
        {/* A categoria ja aparece no titulo do grupo (ou no filtro escolhido). */}
        {s.descricao && <small>{s.descricao}</small>}
      </span>

      <span className="eq-celula">
        {duracaoLegivel(s.duracaoMinutos)}
        {s.intervaloAposMinutos > 0 && <small title="Folga após o atendimento">+{s.intervaloAposMinutos} min de folga</small>}
      </span>

      <span className="eq-celula">
        <span className="mono">{s.precoFormatado}</span>
        {proprios.length > 0 && (
          <small title={proprios.map((p) => `${p.nome}: ${p.precoFormatado}`).join('\n')}>
            {proprios.length} com valor próprio
          </small>
        )}
      </span>

      <span className="eq-celula">
        {s.profissionais.length === 0 ? (
          // Sem ninguem o servico nao aparece para agendar: precisa saltar aos olhos.
          <span className="eq-alerta">Ninguém executa</span>
        ) : (
          <span className="cat-pessoas" title={s.profissionais.map((p) => p.nome).join(', ')}>
            {s.profissionais.slice(0, 4).map((p) => (
              <span key={p.id} className="cat-pessoa" style={{ '--cor': p.cor || 'var(--superficie-3)' }}>
                {p.nome.slice(0, 1).toUpperCase()}
              </span>
            ))}
            {s.profissionais.length > 4 && <span className="cat-pessoa cat-pessoa--mais">+{s.profissionais.length - 4}</span>}
          </span>
        )}
      </span>

      <span className="eq-celula">
        <span className={`eq-situacao${s.ativo ? '' : ' eq-situacao--inativa'}`}>
          <span className={`eq-ponto eq-ponto--${s.ativo ? 'sucesso' : 'neutro'}`} aria-hidden="true" />
          {s.ativo ? 'Ativo' : 'Inativo'}
        </span>
      </span>

      <span className="eq-acoes">
        {podeEditar && (
          <MenuAcoes
            rotulo={`Ações de ${s.nome}`}
            acoes={[
              { rotulo: 'Editar', aoClicar: aoAbrir },
              s.ativo
                ? { rotulo: 'Desativar', aoClicar: aoAlternar }
                : { rotulo: 'Ativar', aoClicar: aoAlternar },
              { rotulo: 'Excluir', perigo: true, aoClicar: aoExcluir }
            ]}
          />
        )}
      </span>
    </div>
  );
}

const NOVA_CATEGORIA = '__nova__';

/** Tipos de atendimento (valores do contrato com a API). */
const TIPOS = [
  { valor: 'consulta', rotulo: 'Consulta' },
  { valor: 'retorno', rotulo: 'Retorno' },
  { valor: 'exame', rotulo: 'Exame' },
  { valor: 'procedimento', rotulo: 'Procedimento' },
  { valor: 'cirurgia', rotulo: 'Cirurgia' },
  { valor: 'bariatrica', rotulo: 'Consulta bariátrica' }
];
const ROTULO_TIPO = Object.fromEntries(TIPOS.map((t) => [t.valor, t.rotulo]));
// Cirurgia e bariatrica nao fecham sozinhas: a equipe confirma antes de valer.
const TIPOS_QUE_EXIGEM_CONFIRMACAO = ['cirurgia', 'bariatrica'];

/** Criar e editar servico: o mesmo formulario. */
function FichaServico({ servico, categoriaInicial, categorias, aoFechar, aoSalvar }) {
  const editando = Boolean(servico);
  const categoriaDeInicio = servico?.categoria ?? categoriaInicial ?? categorias[0] ?? 'Geral';

  const [form, setForm] = useState(() => ({
    nome: servico?.nome ?? '',
    descricao: servico?.descricao ?? '',
    tipo: servico?.tipo ?? 'consulta',
    preparo: servico?.preparo ?? '',
    exigeConfirmacao: servico?.exigeConfirmacao ?? false,
    iaAgenda: servico?.iaAgenda ?? true,
    categoria: categorias.includes(categoriaDeInicio) ? categoriaDeInicio : NOVA_CATEGORIA,
    categoriaNova: categorias.includes(categoriaDeInicio) ? '' : categoriaDeInicio,
    duracaoMinutos: servico?.duracaoMinutos ?? 30,
    intervaloAposMinutos: servico?.intervaloAposMinutos ?? 0,
    preco: servico ? emReais(servico.precoCentavos) : '',
    ativo: servico?.ativo ?? true,
    // Guarda o vinculo inteiro: editar o servico nao pode apagar o preco
    // proprio que um profissional tem nele.
    profissionais: (servico?.profissionais ?? []).map((p) => ({
      professionalId: p.id,
      precoCentavos: p.precoProprio ? p.precoCentavos : null,
      duracaoMinutos: p.duracaoPropria ? p.duracaoMinutos : null
    }))
  }));
  const mudar = (campo) => (e) => setForm({ ...form, [campo]: e.target.value });
  const DIAS_CURTOS = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'];
  const horarioProprio = new Map(
    (servico?.profissionais ?? [])
      .filter((p) => p.jornada)
      .map((p) => [
        p.id,
        Object.entries(p.jornada.dias ?? {})
          .map(([d, faixas]) => `${DIAS_CURTOS[d]} ${faixas.map((f) => `${f.inicio}–${f.fim}`).join(', ')}`)
          .join(' · ')
      ])
  );
  // Ao escolher cirurgia/bariatrica o interruptor liga sozinho; segue editavel.
  const mudarTipo = (e) => {
    const tipo = e.target.value;
    setForm({ ...form, tipo, exigeConfirmacao: TIPOS_QUE_EXIGEM_CONFIRMACAO.includes(tipo) ? true : form.exigeConfirmacao });
  };

  const equipe = useQuery({ queryKey: ['profissionais', 'todos'], queryFn: () => api.get('/api/profissionais', { incluirInativos: 'true' }) });
  const marcados = new Set(form.profissionais.map((p) => p.professionalId));
  // Ativos, mais qualquer inativo que ja esteja ligado (para dar para desligar).
  const pessoas = (equipe.data?.profissionais ?? []).filter((p) => p.ativo || marcados.has(p.id));

  const salvar = useMutation({
    mutationFn: (corpo) => (editando ? api.patch(`/api/servicos/${servico.id}`, corpo) : api.post('/api/servicos', corpo)),
    onSuccess: aoSalvar
  });
  const erros = salvar.error?.camposComErro ?? {};

  function alternarProfissional(id) {
    setForm({
      ...form,
      profissionais: marcados.has(id)
        ? form.profissionais.filter((p) => p.professionalId !== id)
        : [...form.profissionais, { professionalId: id }]
    });
  }

  function enviar() {
    const categoria = form.categoria === NOVA_CATEGORIA ? form.categoriaNova.trim() : form.categoria;
    salvar.mutate({
      nome: form.nome,
      descricao: form.descricao,
      tipo: form.tipo,
      preparo: form.preparo,
      exigeConfirmacao: form.exigeConfirmacao,
      iaAgenda: form.iaAgenda,
      categoria: categoria || 'Geral',
      duracaoMinutos: Number(form.duracaoMinutos),
      intervaloAposMinutos: Number(form.intervaloAposMinutos) || 0,
      precoCentavos: form.preco,
      ativo: form.ativo,
      profissionais: form.profissionais
    });
  }

  return (
    <Modal
      titulo={editando ? `Editar ${servico.nome}` : 'Nova consulta ou procedimento'}
      aberto
      largura={640}
      aoFechar={aoFechar}
      rodape={
        <>
          <Botao variante="secundario" onClick={aoFechar}>
            Cancelar
          </Botao>
          <Botao carregando={salvar.isPending} onClick={enviar}>
            {editando ? 'Salvar alterações' : 'Cadastrar'}
          </Botao>
        </>
      }
    >
      {salvar.isError && salvar.error.codigo !== 'VALIDACAO' && <Aviso tom="perigo">{salvar.error.message}</Aviso>}

      <div className="cat-form">
        <Campo rotulo="Nome" obrigatorio erro={erros.nome}>
          <Entrada value={form.nome} onChange={mudar('nome')} autoFocus={!editando} placeholder="Consulta cardiológica" />
        </Campo>

        <Campo rotulo="Tipo" erro={erros.tipo}>
          <Selecao value={form.tipo} onChange={mudarTipo} aria-label="Tipo de atendimento">
            {TIPOS.map((t) => (
              <option key={t.valor} value={t.valor}>
                {t.rotulo}
              </option>
            ))}
          </Selecao>
        </Campo>

        <Campo rotulo="Categoria" erro={erros.categoria}>
          <Selecao value={form.categoria} onChange={mudar('categoria')} aria-label="Categoria">
            {categorias.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
            <option value={NOVA_CATEGORIA}>+ Nova categoria…</option>
          </Selecao>
          {form.categoria === NOVA_CATEGORIA && (
            <Entrada
              className="cat-form__nova"
              value={form.categoriaNova}
              onChange={mudar('categoriaNova')}
              placeholder="Nome da nova categoria"
              maxLength={60}
              aria-label="Nome da nova categoria"
            />
          )}
        </Campo>

        <Campo
          rotulo="Preço particular"
          obrigatorio
          erro={erros.precoCentavos}
          dica={
            form.tipo === 'retorno'
              ? 'Valor particular. O retorno é gratuito dentro do prazo do médico (veja Equipe); fora do prazo, vale como consulta.'
              : 'Valor PARTICULAR. Atendimento por convênio não usa este preço. Pode digitar 250,00 ou R$ 250,00.'
          }
        >
          <Entrada value={form.preco} onChange={mudar('preco')} placeholder="250,00" inputMode="decimal" />
        </Campo>

        <Campo rotulo="Duração (minutos)" obrigatorio erro={erros.duracaoMinutos}>
          <Entrada type="number" min="5" max="600" value={form.duracaoMinutos} onChange={mudar('duracaoMinutos')} />
        </Campo>

        <Campo rotulo="Folga após (minutos)" dica="Higienização da sala ou preparo. Ocupa a agenda, não é cobrado." erro={erros.intervaloAposMinutos}>
          <Entrada type="number" min="0" max="120" value={form.intervaloAposMinutos} onChange={mudar('intervaloAposMinutos')} />
        </Campo>

        <Campo rotulo="Pré-agendamento" dica="Quando a IA marca, a equipe confirma antes de valer (pela tela vale direto). Liga sozinho em cirurgia e consulta bariátrica.">
          <label className="cat-interruptor">
            <input type="checkbox" checked={form.exigeConfirmacao} onChange={(e) => setForm({ ...form, exigeConfirmacao: e.target.checked })} />
            <span>{form.exigeConfirmacao ? 'Sim, a equipe confirma' : 'Agenda direto'}</span>
          </label>
        </Campo>

        <Campo rotulo="A Sofia pode agendar" dica="Desligado: a Sofia informa o serviço e passa o paciente para a recepção marcar (ex.: consulta particular, consulta Hapvida).">
          <label className="cat-interruptor">
            <input type="checkbox" checked={form.iaAgenda} onChange={(e) => setForm({ ...form, iaAgenda: e.target.checked })} />
            <span>{form.iaAgenda ? 'Sim, ela marca sozinha' : 'Não, só a recepção'}</span>
          </label>
        </Campo>

        <Campo rotulo="Situação" dica="Inativo some da agenda e da Sofia.">
          <label className="cat-interruptor">
            <input type="checkbox" checked={form.ativo} onChange={(e) => setForm({ ...form, ativo: e.target.checked })} />
            <span>{form.ativo ? 'Ativo' : 'Inativo'}</span>
          </label>
        </Campo>
      </div>

      <Campo rotulo="Descrição" dica="Opcional. A Sofia busca por ela também: escreva como o paciente fala (ex.: check-up, eletro, ver o coração, pedir exame).">
        <AreaTexto rows={2} value={form.descricao} onChange={mudar('descricao')} maxLength={1000} />
      </Campo>

      <Campo rotulo="Preparo" dica="A Sofia envia ao paciente depois de agendar: jejum, documentos, pedido médico, guia do convênio.">
        <AreaTexto rows={3} value={form.preparo} onChange={mudar('preparo')} maxLength={2000} placeholder="Ex.: Jejum de 8 horas. Trazer documento com foto, pedido médico e a guia do convênio." />
      </Campo>

      <Campo rotulo="Quem executa" dica="Sem ninguém marcado, o serviço não aparece na tela de agendar. Preço próprio de cada um se ajusta na Equipe.">
        {equipe.isLoading ? (
          <Carregando />
        ) : pessoas.length === 0 ? (
          <p className="texto-fraco">Nenhum profissional cadastrado ainda — cadastre na Equipe.</p>
        ) : (
          <div className="eq-chips">
            {pessoas.map((p) => (
              <button
                key={p.id}
                type="button"
                aria-pressed={marcados.has(p.id)}
                className={`eq-chip${marcados.has(p.id) ? ' eq-chip--ativo' : ''}`}
                onClick={() => alternarProfissional(p.id)}
              >
                <span className="cat-pessoa cat-pessoa--mini" style={{ '--cor': p.cor || 'var(--superficie-3)' }} aria-hidden="true" />
                {p.nome}
                {!p.ativo && <small>inativo</small>}
                {/* Horario proprio deste servico (ex.: ligadura so sexta 11h): a agenda usa ele, nao a jornada. */}
                {horarioProprio.has(p.id) && <small title={horarioProprio.get(p.id)}>horário próprio</small>}
              </button>
            ))}
          </div>
        )}
      </Campo>
    </Modal>
  );
}

function ModalRenomearCategoria({ atual, categorias, aoFechar, aoSalvar }) {
  const [nome, setNome] = useState(atual);
  const renomear = useMutation({
    mutationFn: () => api.post('/api/servicos/categorias/renomear', { de: atual, para: nome.trim() }),
    onSuccess: () => aoSalvar(nome.trim())
  });
  const juntaCom = nome.trim() !== atual && categorias.find((c) => c === nome.trim());

  return (
    <Modal
      titulo={`Renomear "${atual}"`}
      aberto
      aoFechar={aoFechar}
      rodape={
        <>
          <Botao variante="secundario" onClick={aoFechar}>
            Cancelar
          </Botao>
          <Botao carregando={renomear.isPending} disabled={!nome.trim() || nome.trim() === atual} onClick={() => renomear.mutate()}>
            {juntaCom ? 'Juntar categorias' : 'Renomear'}
          </Botao>
        </>
      }
    >
      {renomear.isError && <Aviso tom="perigo">{renomear.error.message}</Aviso>}
      <Campo rotulo="Novo nome" dica="Todos os serviços desta categoria passam para o novo nome.">
        <Entrada value={nome} onChange={(e) => setNome(e.target.value)} maxLength={60} autoFocus />
      </Campo>
      {juntaCom && (
        <Aviso tom="alerta">
          Já existe a categoria "{juntaCom}". Os serviços de "{atual}" vão para ela e as duas viram uma só.
        </Aviso>
      )}
    </Modal>
  );
}
